import React, { useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Download, Calendar, CalendarOff, Flag, Trash2, Tag, Share2, FolderInput, BookmarkPlus } from 'lucide-react';
import { DateModal } from '@/design-system/patterns/forms/DateModal';
import { ConfirmActionModal } from '@/design-system/patterns/forms/ConfirmActionModal';
import { AddToBaulModal } from '@/features/photos/components/AddToBaulModal';
import { DeletePhotoModal } from '@/features/photos/components/DeletePhotoModal';
import { RemovalRequestModal } from '@/features/moderation/components/RemovalRequestModal';
import { TagPersonasModal } from '@/features/photos/components/TagPersonasModal';
import { PhotoViewerMenuItem } from '@/features/photos/components/PhotoViewerHeader';
import { Persona, Photo, PhotoDate, Recuerdo, TaggedPersona } from '@/types';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useUIStore } from '@/store/uiStore';
import { useBaulesStore } from '@/store/useBaulesStore';
import { submitRemovalRequest } from '@/features/moderation/useCases';
import { setTaggedPersonas, deletePhoto, changePhotoDate, clearPhotoDate, addPhotoToBaul, saveToMyPhotos } from '@/features/photos/useCases';
import { addRecuerdo as addRecuerdoUseCase, editRecuerdo as editRecuerdoUseCase } from '@/features/memories/useCases';
import { createPersona } from '@/features/people/useCases';
import { api } from '@/api';
import { saveDownloadedPhoto } from '@/utils/downloadFile';
import { Capacitor } from '@capacitor/core';
import { sharePublicLink } from '@/features/sharing/sharePublicLink';
import { usePostHog } from 'posthog-js/react';

interface UsePhotoViewerActionsOptions {
  baulId: string;
  baulName: string;
  photo: Photo;
  sharedLinksEnabled: boolean;
  baulPersonas?: Persona[];
  taggedPersonas?: TaggedPersona[];
  /** Se invoca tras retirar la foto con éxito, para cerrar el visor (la foto ya no existe).
   * En la práctica es el mismo callback que cierra el visor por cualquier otro motivo — ver
   * PhotoViewerContainer, que le pasa directamente su propio onClose. */
  onDeleted: () => void;
}

interface UsePhotoViewerActionsResult {
  /** Construye el array final de items del menú "···". `extraItems` son las acciones que
   * solo tienen sentido para quien nos monta (p.ej. mover de capítulo — exclusiva de
   * ChapterPhotoViewerContainer/CrossChapterPhotoViewerContainer, que no conocen esta hook en
   * absoluto). Se intercalan antes de las acciones destructivas, que van siempre al final sin
   * importar qué se les pase — así ningún caller puede romper ese orden por accidente. */
  buildMenuItems: (extraItems?: PhotoViewerMenuItem[]) => PhotoViewerMenuItem[];
  canChangeDate: boolean;
  openDateModal: () => void;
  modals: React.ReactNode;
  onAddRecuerdo: (text: string) => void;
  onEditRecuerdo: (recuerdo: Recuerdo, text: string) => Promise<boolean>;
  onShareRecuerdo: (recuerdo: Recuerdo) => void;
}

// Acciones "···" del visor de fotos, más añadir/editar/compartir recuerdo (antes duplicadas
// en cada XxxPhotoViewerRoute) — todo en un único sitio, sin saber nada de capítulos: mover
// vive en ChapterPhotoViewerContainer/CrossChapterPhotoViewerContainer, un nivel por encima, y
// se intercala vía buildMenuItems.
// Ni PhotoViewer (puro) ni PhotoViewerContainer (universal) importan store/useCases/router
// para nada de esto.
export function usePhotoViewerActions({
  baulId, baulName, photo, sharedLinksEnabled, baulPersonas = [], taggedPersonas = [], onDeleted,
}: UsePhotoViewerActionsOptions): UsePhotoViewerActionsResult {
  const auth = useAuth();
  const { run } = useAsyncAction();
  const posthog = usePostHog();
  const showToastMessage = useUIStore((state) => state.showToastMessage);
  const hasRequestedRemoval = useUIStore((state) => state.hasRequestedPhotoRemoval(photo.id));
  const markPhotoRemovalRequested = useUIStore((state) => state.markPhotoRemovalRequested);
  // Baúles a los que este usuario puede añadir contenido, excluyendo el que ya tiene esta foto —
  // ver docs/.backlog issue #62 §"Authorization": el backend es quien de verdad autoriza cada
  // baúl al confirmar, esta lista solo evita ofrecer opciones sin sentido en el picker.
  // useMemo (no derivar el array directamente en el selector) evita que Zustand vea una
  // referencia nueva en cada render y entre en bucle de re-renders.
  const allBaules = useBaulesStore((state) => state.baules);
  const otherBaules = useMemo(() => allBaules.filter((b) => b.id !== baulId), [allBaules, baulId]);

  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showClearDateModal, setShowClearDateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showAddToBaulModal, setShowAddToBaulModal] = useState(false);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);
  const [selectedTargetBaulId, setSelectedTargetBaulId] = useState('');
  const [isSubmittingRemoval, setIsSubmittingRemoval] = useState(false);
  const [isSubmittingDate, setIsSubmittingDate] = useState(false);
  const [isClearingDate, setIsClearingDate] = useState(false);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [isSubmittingTags, setIsSubmittingTags] = useState(false);
  const [isAddingToBaul, setIsAddingToBaul] = useState(false);
  // "Guardar en Mis fotos" (Slice 5, docs/.backlog issue #62) — sin flag precomputado por el
  // backend (ver el comentario de PhotoDto): este estado local solo refleja qué fotos ya se
  // pulsaron en esta misma sesión de visor, para evitar una escritura redundante visible y
  // mostrar "Guardada en Mis fotos"; el backend es igualmente idempotente si se pulsa de nuevo.
  // Indexado por photo.id (no un único booleano) — este hook se monta una sola vez por sesión
  // de visor (ver PhotoViewerContainer) y `photo` cambia al deslizar entre fotos sin remontar,
  // así que un booleano sin ámbito se arrastraría indefinidamente a cualquier otra foto (#92).
  const [savedPhotoIds, setSavedPhotoIds] = useState<Set<string>>(new Set());
  const [savingPhotoId, setSavingPhotoId] = useState<string | null>(null);
  const savedToMyPhotos = savedPhotoIds.has(photo.id);
  const isSavingToMyPhotos = savingPhotoId === photo.id;

  const openTagModal = () => {
    setSelectedPersonaIds(taggedPersonas.map((p) => p.id));
    setShowTagModal(true);
  };

  const toggleTaggedPersona = (personaId: string) => {
    setSelectedPersonaIds((current) =>
      current.includes(personaId) ? current.filter((id) => id !== personaId) : [...current, personaId]);
  };

  const handleTagsSubmit = async () => {
    setIsSubmittingTags(true);
    const result = await run(() => setTaggedPersonas(photo.id, selectedPersonaIds), {
      successMessage: 'Personas etiquetadas actualizadas',
      errorMessage: 'Error al etiquetar personas',
    });
    setIsSubmittingTags(false);
    if (result.ok) {
      posthog.capture('person_tagged', { photo_count: 1 });
      setShowTagModal(false);
    }
  };

  // "Crear nueva persona"/"Crear y etiquetar" inline de TagPersonasModal (alternativa 1d) — key
  // propia para no bloquearse con otra acción en curso bajo la key por defecto.
  const handleCreatePersona = async (nickname: string): Promise<Persona | undefined> => {
    const result = await run(() => createPersona(baulId, nickname), {
      key: 'create-persona',
      errorMessage: 'Error al añadir la persona',
    });
    if (result.ok) posthog.capture('persona_created', { source: 'tag_personas' });
    return result.ok ? result.value : undefined;
  };

  const handleSharePhoto = async () => {
    const result = await run(() => api.photos.createShareLink(photo.id), {
      key: 'share-photo',
      errorMessage: 'Error al crear el enlace',
    });
    if (!result.ok) return;
    posthog.capture('photo_shared');
    await sharePublicLink({
      title: `Foto de ${baulName}`,
      text: `Te comparto una foto de "${baulName}" en El Baúl.`,
      url: result.value.url,
      onCopied: () => showToastMessage('Enlace copiado al portapapeles'),
    });
  };

  const handleDownloadPhoto = async () => {
    const result = await run(async () => {
      const { blob, fileName } = await api.photos.download(photo.id);
      await saveDownloadedPhoto(blob, fileName);
    }, {
      successMessage: Capacitor.isNativePlatform() ? 'Foto guardada en la galería' : undefined,
      errorMessage: 'Error al descargar la foto',
    });
    if (result.ok) posthog.capture('photo_downloaded');
  };

  const handleDateSubmit = async (date: PhotoDate) => {
    setIsSubmittingDate(true);
    const result = await run(() => changePhotoDate(baulId, photo.id, date), {
      successMessage: 'Fecha actualizada',
      errorMessage: 'Error al cambiar la fecha',
    });
    setIsSubmittingDate(false);
    if (result.ok) {
      posthog.capture('photo_date_changed', { action: 'set' });
      setShowDateModal(false);
    }
  };

  const handleClearDateConfirm = async () => {
    setIsClearingDate(true);
    const result = await run(() => clearPhotoDate(baulId, photo.id), {
      successMessage: 'Fecha borrada',
      errorMessage: 'Error al borrar la fecha',
    });
    setIsClearingDate(false);
    if (result.ok) {
      posthog.capture('photo_date_changed', { action: 'clear' });
      setShowClearDateModal(false);
    }
  };

  const handleDeleteSubmit = async (reason: string) => {
    setIsDeletingPhoto(true);
    const result = await run(() => deletePhoto(baulId, photo.id, reason), {
      successMessage: 'La foto ha sido borrada',
      errorMessage: 'Error al borrar la foto',
    });
    setIsDeletingPhoto(false);
    if (result.ok) {
      posthog.capture('photo_deleted');
      setShowDeleteModal(false);
      onDeleted();
    }
  };

  const openAddToBaulModal = () => {
    setSelectedTargetBaulId('');
    setShowAddToBaulModal(true);
  };

  const handleAddToBaulConfirm = async () => {
    if (!selectedTargetBaulId) return;
    setIsAddingToBaul(true);
    const result = await run(() => addPhotoToBaul(photo.id, selectedTargetBaulId), {
      successMessage: 'Foto añadida al baúl',
      errorMessage: 'Error al añadir la foto al baúl',
    });
    setIsAddingToBaul(false);
    if (result.ok) {
      posthog.capture('photo_added_to_baul');
      setShowAddToBaulModal(false);
    }
  };

  const handleSaveToMyPhotos = async () => {
    const savedPhotoId = photo.id;
    setSavingPhotoId(savedPhotoId);
    const result = await run(() => saveToMyPhotos(savedPhotoId), {
      successMessage: 'Foto guardada en Mis fotos',
      errorMessage: 'Error al guardar la foto en Mis fotos',
    });
    setSavingPhotoId((current) => (current === savedPhotoId ? null : current));
    if (result.ok) {
      posthog.capture('photo_saved_to_personal');
      setSavedPhotoIds((current) => new Set(current).add(savedPhotoId));
    }
  };

  const handleSubmitRemoval = async (reason: string) => {
    setIsSubmittingRemoval(true);
    const result = await run(() => submitRemovalRequest(baulId, photo, reason), {
      successMessage: 'Tu solicitud ha sido enviada',
      errorMessage: 'Error al enviar la solicitud',
    });
    setIsSubmittingRemoval(false);
    if (result.ok) {
      posthog.capture('photo_removal_requested');
      setShowRemovalModal(false);
      markPhotoRemovalRequested(photo.id);
    }
  };

  const handleAddRecuerdo = (text: string) => {
    if (!auth.isAuthenticated) return;
    run(() => addRecuerdoUseCase(baulId, photo.id, text), { errorMessage: 'Error al añadir el recuerdo' })
      .then((result) => {
        if (result.ok) posthog.capture('recuerdo_created', { source: 'photo_viewer' });
      });
  };

  const handleEditRecuerdo = async (recuerdo: Recuerdo, text: string): Promise<boolean> => {
    if (!auth.isAuthenticated) return false;
    const result = await run(() => editRecuerdoUseCase(recuerdo.id, text), {
      successMessage: 'Recuerdo actualizado',
      errorMessage: 'Error al guardar el recuerdo',
    });
    if (result.ok) posthog.capture('recuerdo_edited');
    return result.ok;
  };

  const handleShareRecuerdo = async (recuerdo: Recuerdo) => {
    const result = await run(() => api.recuerdos.createShareLink(recuerdo.id), {
      key: 'share-recuerdo',
      errorMessage: 'Error al crear el enlace',
    });
    if (!result.ok) return;
    posthog.capture('recuerdo_shared');
    await sharePublicLink({
      title: `Recuerdo de ${baulName}`,
      text: `Te comparto un recuerdo de "${baulName}" en El Baúl.`,
      url: result.value.url,
      onCopied: () => showToastMessage('Enlace copiado al portapapeles'),
    });
  };

  const buildMenuItems = (extraItems: PhotoViewerMenuItem[] = []): PhotoViewerMenuItem[] => {
    const items: PhotoViewerMenuItem[] = [
      { key: 'tag-personas', label: 'Etiquetar personas', icon: Tag, onSelect: openTagModal },
    ];
    if (sharedLinksEnabled) {
      items.push({ key: 'share', label: 'Compartir foto', icon: Share2, onSelect: handleSharePhoto });
    }
    items.push({ key: 'download', label: 'Descargar foto original', icon: Download, onSelect: handleDownloadPhoto });
    items.push({
      key: 'save-to-my-photos',
      label: savedToMyPhotos ? 'Guardada en Mis fotos' : 'Guardar en Mis fotos',
      icon: BookmarkPlus,
      onSelect: handleSaveToMyPhotos,
      disabled: savedToMyPhotos || isSavingToMyPhotos,
    });
    if (otherBaules.length > 0) {
      items.push({ key: 'add-to-baul', label: 'Añadir a otro baúl', icon: FolderInput, onSelect: openAddToBaulModal });
    }
    items.push(...extraItems);
    items.push({ key: 'date', label: 'Cambiar fecha', icon: Calendar, onSelect: () => setShowDateModal(true) });
    if (photo.date) {
      // Variant por defecto (no destructiva) a propósito: aunque borra la fecha, no debe
      // competir en protagonismo visual con "Borrar foto", la única acción realmente
      // destructiva de este menú.
      items.push({ key: 'clear-date', label: 'Borrar fecha', icon: CalendarOff, onSelect: () => setShowClearDateModal(true) });
    }
    // Destructivas siempre al final, sin importar qué extras se hayan intercalado arriba.
    // canDelete/canRequestRemoval vienen calculadas por el backend (nunca ambas a true) — ver
    // PhotoDto/PhotoDeletePolicy. El frontend solo pinta lo que el backend ya decidió, sin
    // reconstruir aquí la regla de admin/propietario/ventana de gracia.
    if (photo.canRequestRemoval) {
      items.push(hasRequestedRemoval
        ? { key: 'removal', label: 'Ya has solicitado la retirada', icon: Flag, onSelect: () => {}, disabled: true }
        : { key: 'removal', label: 'Solicitar retirada', icon: Flag, onSelect: () => setShowRemovalModal(true) });
    }
    if (photo.canDelete) {
      items.push({ key: 'delete', label: 'Borrar foto', icon: Trash2, onSelect: () => setShowDeleteModal(true), variant: 'destructive' });
    }
    return items;
  };

  const modals = (
    <>
      {showRemovalModal && (
        <RemovalRequestModal
          onCancel={() => setShowRemovalModal(false)}
          onConfirm={handleSubmitRemoval}
          isSubmitting={isSubmittingRemoval}
        />
      )}

      {showDateModal && (
        <DateModal
          title="Cambiar fecha de la foto"
          onCancel={() => setShowDateModal(false)}
          onConfirm={handleDateSubmit}
          isSubmitting={isSubmittingDate}
          initialValue={photo.date}
        />
      )}

      {showClearDateModal && (
        <ConfirmActionModal
          title="Borrar fecha de la foto"
          tone="plain"
          description="La foto dejará de tener una fecha asignada. Podrás añadir una nueva más adelante."
          confirmLabel="Sí, borrar fecha"
          confirmVariant="primary"
          onCancel={() => setShowClearDateModal(false)}
          onConfirm={handleClearDateConfirm}
          isSubmitting={isClearingDate}
        />
      )}

      {showDeleteModal && (
        <DeletePhotoModal
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteSubmit}
          isSubmitting={isDeletingPhoto}
        />
      )}

      {showAddToBaulModal && (
        <AddToBaulModal
          baules={otherBaules}
          selectedId={selectedTargetBaulId}
          onSelect={setSelectedTargetBaulId}
          onCancel={() => setShowAddToBaulModal(false)}
          onConfirm={handleAddToBaulConfirm}
          isSubmitting={isAddingToBaul}
        />
      )}

      {showTagModal && (
        <TagPersonasModal
          personas={baulPersonas}
          selectedIds={selectedPersonaIds}
          onToggle={toggleTaggedPersona}
          onCreatePersona={handleCreatePersona}
          onCancel={() => setShowTagModal(false)}
          onConfirm={handleTagsSubmit}
          isSubmitting={isSubmittingTags}
        />
      )}
    </>
  );

  return {
    buildMenuItems,
    // Cambiar fecha ya no depende de estar dentro de un capítulo — universal en cualquier
    // visor. Se mantiene como campo explícito (en vez de asumir siempre true en PhotoViewer)
    // por si algún visor futuro necesita desactivarlo.
    canChangeDate: true,
    openDateModal: () => setShowDateModal(true),
    modals,
    onAddRecuerdo: handleAddRecuerdo,
    onEditRecuerdo: handleEditRecuerdo,
    onShareRecuerdo: handleShareRecuerdo,
  };
}
